/* ==========================================================================
   Admin Team Management Page — PORTAL-94
   Invite and manage additional pharmacy logins (Admin / Manager / Staff).
   Visible to Admins and Managers only (route-guarded); the backend is the
   authoritative guard for every action here.
   ========================================================================== */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserPlus, MoreVertical } from 'lucide-react'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { AdminTableSkeleton } from '@/components/skeletons'
import { InvitePharmacyUserModal } from '@/components/admin/InvitePharmacyUserModal'
import { useAdminAuth, useAdminPermissions } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  listPharmacyUsers,
  invitePharmacyUser,
  updatePharmacyUser,
  resendPharmacyUserInvite,
  deletePharmacyUser,
  resetPharmacyUserTwoFactor,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import {
  PharmacyUserRole,
  PharmacyUserStatus,
  PHARMACY_USER_ROLE_LABELS,
  type PharmacyUserResponse,
  type PharmacyUserInvite,
} from '@/api/types'

const ALL_ROLES: PharmacyUserRole[] = [
  PharmacyUserRole.ADMIN,
  PharmacyUserRole.MANAGER,
  PharmacyUserRole.STAFF,
]

function RoleBadge({ role }: { role: PharmacyUserRole }) {
  const styles: Record<PharmacyUserRole, string> = {
    admin: 'bg-deep-purple/10 text-deep-purple',
    manager: 'bg-blue-100 text-blue-800',
    staff: 'bg-gray-100 text-gray-700',
  }
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[role]}`}
    >
      {PHARMACY_USER_ROLE_LABELS[role]}
    </span>
  )
}

function StatusBadge({ status }: { status: PharmacyUserStatus }) {
  switch (status) {
    case PharmacyUserStatus.ACTIVE:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      )
    case PharmacyUserStatus.INVITED:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Invited
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Disabled
        </span>
      )
  }
}

export function Team() {
  const { user, pharmacy } = useAdminAuth()
  const { isAdmin } = useAdminPermissions()
  const { success: showSuccess, error: showError } = useToast()
  const slug = pharmacy?.portal_slug ?? ''

  const [users, setUsers] = useState<PharmacyUserResponse[]>([])
  const [limits, setLimits] = useState<Record<PharmacyUserRole, number> | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<PharmacyUserResponse | null>(null)
  const [resetTwoFactorTarget, setResetTwoFactorTarget] =
    useState<PharmacyUserResponse | null>(null)

  // ``showSkeleton`` is opt-in so only the first load blanks the table. Refreshes
  // after a mutation update in place — the row-level ``busyId`` spinner already
  // shows progress, and swapping the whole table for a skeleton each time is jarring.
  const fetchUsers = useCallback(
    async ({ showSkeleton = false }: { showSkeleton?: boolean } = {}) => {
      if (!slug) return
      if (showSkeleton) setLoading(true)
      try {
        const result = await listPharmacyUsers(slug)
        setUsers(result.users)
        setLimits(result.limits)
        setLoadError(null)
      } catch (err) {
        setLoadError(
          err instanceof ApiClientError ? err.message : 'Failed to load team members'
        )
      } finally {
        if (showSkeleton) setLoading(false)
      }
    },
    [slug]
  )

  useEffect(() => {
    fetchUsers({ showSkeleton: true })
  }, [fetchUsers])

  // Non-disabled counts per role, for the cap summary.
  const counts = useMemo(() => {
    const c: Record<PharmacyUserRole, number> = { admin: 0, manager: 0, staff: 0 }
    for (const u of users) {
      if (u.status !== PharmacyUserStatus.DISABLED) c[u.role] += 1
    }
    return c
  }, [users])

  // The roles the current user may invite/assign (Admin: all; Manager: staff only).
  const assignableRoles = useMemo(
    () => (isAdmin ? ALL_ROLES : [PharmacyUserRole.STAFF]),
    [isAdmin]
  )

  // Mirror the backend rule: Admins manage anyone; Managers manage only Staff.
  const canManage = useCallback(
    (target: PharmacyUserResponse) =>
      isAdmin || target.role === PharmacyUserRole.STAFF,
    [isAdmin]
  )

  async function runAction(fn: () => Promise<void>, successMsg: string, id: string) {
    setBusyId(id)
    try {
      await fn()
      showSuccess(successMsg)
      await fetchUsers()
    } catch (err) {
      showError(
        err instanceof ApiClientError ? err.message : 'Something went wrong. Please try again.'
      )
    } finally {
      setBusyId(null)
    }
  }

  async function handleInvite(data: PharmacyUserInvite) {
    setIsSubmitting(true)
    try {
      await invitePharmacyUser(slug, data)
      showSuccess('Invitation sent successfully')
      setIsInviteOpen(false)
      await fetchUsers()
    } catch (err) {
      showError(
        err instanceof ApiClientError ? err.message : 'Failed to send invitation. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleChangeRole(target: PharmacyUserResponse, role: PharmacyUserRole) {
    void runAction(
      () => updatePharmacyUser(slug, target.id, { role }).then(() => undefined),
      `${target.name} is now ${PHARMACY_USER_ROLE_LABELS[role]}`,
      target.id
    )
  }

  function handleToggleStatus(target: PharmacyUserResponse) {
    const nextStatus =
      target.status === PharmacyUserStatus.DISABLED
        ? PharmacyUserStatus.ACTIVE
        : PharmacyUserStatus.DISABLED
    void runAction(
      () => updatePharmacyUser(slug, target.id, { status: nextStatus }).then(() => undefined),
      nextStatus === PharmacyUserStatus.DISABLED
        ? `${target.name} has been disabled`
        : `${target.name} has been re-enabled`,
      target.id
    )
  }

  function handleResend(target: PharmacyUserResponse) {
    void runAction(
      () => resendPharmacyUserInvite(slug, target.id),
      'Invitation resent',
      target.id
    )
  }

  async function handleConfirmRemove() {
    if (!removeTarget) return
    const target = removeTarget
    setRemoveTarget(null)
    await runAction(
      () => deletePharmacyUser(slug, target.id),
      `Invitation for ${target.name} has been cancelled`,
      target.id
    )
  }

  async function handleConfirmResetTwoFactor() {
    if (!resetTwoFactorTarget) return
    const target = resetTwoFactorTarget
    setResetTwoFactorTarget(null)
    await runAction(
      () => resetPharmacyUserTwoFactor(slug, target.id),
      `Two-factor authentication reset for ${target.name}`,
      target.id
    )
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-charcoal">Team</h1>
          <p className="mt-1 text-sm text-charcoal/70">
            Invite and manage the people who can access this pharmacy&apos;s admin
            portal.
          </p>
        </div>
        <Button variant="primary" onClick={() => setIsInviteOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite
        </Button>
      </div>

      {/* Cap summary */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        {ALL_ROLES.map((role) => (
          <Card key={role} className="p-4">
            <p className="text-xs uppercase tracking-wide text-charcoal/60">
              {PHARMACY_USER_ROLE_LABELS[role]}s
            </p>
            <p className="mt-1 text-lg font-semibold text-charcoal">
              {counts[role]}
              {limits && (
                <span className="text-sm font-normal text-charcoal/50">
                  {' '}
                  / {limits[role]}
                </span>
              )}
            </p>
          </Card>
        ))}
      </div>

      {loadError && (
        <Alert variant="error" className="mb-4">
          {loadError}
        </Alert>
      )}

      {loading ? (
        <AdminTableSkeleton />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {users.map((u) => {
                  const isSelf = u.id === user?.id
                  const manageable = canManage(u) && !isSelf
                  const roleTargets = assignableRoles.filter((r) => r !== u.role)
                  const showMenu = manageable
                  return (
                    <tr key={u.id}>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-charcoal">
                        {u.name}
                        {isSelf && (
                          <span className="ml-2 text-xs text-charcoal/50">(you)</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-charcoal/80">
                        {u.email}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        {showMenu ? (
                          <Menu as="div" className="relative inline-block text-left">
                            <MenuButton
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                              disabled={busyId !== null}
                              aria-label={`Actions for ${u.name}`}
                            >
                              <MoreVertical className="h-5 w-5" />
                            </MenuButton>
                            {/* Anchored (portaled) rather than absolutely
                                positioned: the table sits inside overflow-hidden +
                                overflow-x-auto, which clip an absolute child. Same
                                fix as the Catalog row actions. */}
                            <MenuItems
                              anchor="bottom end"
                              className="z-50 w-48 [--anchor-gap:4px] rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none"
                            >
                              {u.status === PharmacyUserStatus.INVITED && (
                                <MenuItem>
                                  <button
                                    onClick={() => handleResend(u)}
                                    className="block w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-gray-50 data-[focus]:bg-gray-50"
                                  >
                                    Resend invitation
                                  </button>
                                </MenuItem>
                              )}
                              {roleTargets.map((r) => (
                                <MenuItem key={r}>
                                  <button
                                    onClick={() => handleChangeRole(u, r)}
                                    className="block w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-gray-50 data-[focus]:bg-gray-50"
                                  >
                                    Change to {PHARMACY_USER_ROLE_LABELS[r]}
                                  </button>
                                </MenuItem>
                              ))}
                              {u.status !== PharmacyUserStatus.INVITED && (
                                <MenuItem>
                                  <button
                                    onClick={() => handleToggleStatus(u)}
                                    className="block w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-gray-50 data-[focus]:bg-gray-50"
                                  >
                                    {u.status === PharmacyUserStatus.DISABLED
                                      ? 'Re-enable'
                                      : 'Disable'}
                                  </button>
                                </MenuItem>
                              )}
                              {/* Lost phone (PORTAL-187): clears their 2FA so they
                                  can sign in with just their password again. */}
                              {u.two_factor_enabled && (
                                <MenuItem>
                                  <button
                                    onClick={() => setResetTwoFactorTarget(u)}
                                    className="block w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-gray-50 data-[focus]:bg-gray-50"
                                  >
                                    Reset two-factor
                                  </button>
                                </MenuItem>
                              )}
                              {/* Deletion is only for undoing a mis-sent invite —
                                  an accepted teammate is disabled instead. */}
                              {u.status === PharmacyUserStatus.INVITED && (
                                <MenuItem>
                                  <button
                                    onClick={() => setRemoveTarget(u)}
                                    className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 data-[focus]:bg-red-50"
                                  >
                                    Cancel invitation
                                  </button>
                                </MenuItem>
                              )}
                            </MenuItems>
                          </Menu>
                        ) : (
                          <span className="text-xs text-charcoal/40">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <InvitePharmacyUserModal
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSubmit={handleInvite}
        allowedRoles={assignableRoles}
        loading={isSubmitting}
      />

      <Modal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Cancel invitation"
        size="sm"
      >
        <p className="text-sm text-charcoal/80">
          Cancel the invitation for{' '}
          <span className="font-medium">{removeTarget?.name}</span>? Their invitation
          link will stop working and the email address will be free to invite again.
          This cannot be undone.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
          {/* Not "Cancel" / "Remove": under a "Cancel invitation" title, a button
              labelled Cancel reads as the destructive action. Name both outcomes. */}
          <Button
            variant="secondary"
            onClick={() => setRemoveTarget(null)}
            className="sm:flex-1"
          >
            Keep invitation
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmRemove}
            className="sm:flex-1"
          >
            Cancel invitation
          </Button>
        </div>
      </Modal>

      <Modal
        open={resetTwoFactorTarget !== null}
        onClose={() => setResetTwoFactorTarget(null)}
        title="Reset two-factor authentication"
        size="sm"
      >
        <p className="text-sm text-charcoal/80">
          Turn off two-factor authentication for{' '}
          <span className="font-medium">{resetTwoFactorTarget?.name}</span>? Use this if
          they lost access to their authenticator app. They will sign in with just their
          password and can set it up again from My Account.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
          <Button
            variant="secondary"
            onClick={() => setResetTwoFactorTarget(null)}
            className="sm:flex-1"
          >
            Keep it on
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmResetTwoFactor}
            className="sm:flex-1"
          >
            Reset two-factor
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default Team
